import { CHAT_MARKDOWN_SANITIZE_SCHEMA } from "../markdownSanitization";
import { GITHUB_ALERT_PRESENTATIONS } from "./chat/markdownAlertPresentations";
import { parseAssistantCitationHref } from "@cmmd-forge/shared/assistantCitations";
import { AssistantCitationChip } from "./chat/AssistantCitationChip";
import { DiffsHighlighter, getSharedHighlighter, SupportedLanguages } from "@pierre/diffs";
import { parseScopedThreadKey } from "@cmmd-forge/client-runtime";
import { CheckIcon, CopyIcon, ChevronRightIcon, Maximize2Icon, Minimize2Icon } from "lucide-react";
import React, {
  Children,
  createContext,
  isValidElement,
  Suspense,
  use,
  useCallback,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "@tanstack/react-router";
import { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "~/lib/utils";
import type {
  EnvironmentId,
  FileManagerRevealKind,
  ServerProviderSkill,
} from "@cmmd-forge/contracts";
import { renderSkillInlineMarkdownChildren } from "./chat/SkillInlineText";
import { remarkGithubAlerts } from "../markdown-github-alerts";
import { resolveInlineCodeFileLinkMeta } from "../markdown-inline-code-links";
import { ExplicitMarkdownFileLink, openMarkdownFileLink } from "./chat/ExplicitMarkdownFileLink";
import { MarkdownFileLink } from "./chat/MarkdownFileLink";
import { remarkNormalizeListItemIndentation } from "../markdown-list-indentation";
import {
  extractFenceTitle,
  extractPreCodeMeta,
  remarkPreserveCodeMeta,
  remarkTagInlineCode,
} from "../markdown-code-meta";
import { getVscodeIconUrlForEntry, inferEntryKindFromPath } from "~/vscode-icons";
import { serializeTableElementToCsv, serializeTableElementToMarkdown } from "../markdown-clipboard";
import { orderedListGutterStyle } from "../markdown-list-gutter";
import { Button } from "./ui/button";
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from "./ui/collapsible";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "./ui/menu";
import { ScrollArea } from "./ui/scroll-area";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";
import { resolveDiffThemeName, type DiffThemeName } from "../lib/diffRendering";
import { LRUCache } from "../lib/lruCache";
import { useTheme } from "../hooks/useTheme";
import {
  resolveMarkdownFileLinkTarget,
  rewriteMarkdownFileUriHref,
  type MarkdownFileLinkMeta,
} from "../markdown-links";
import { readLocalApi } from "../localApi";
import { readEnvironmentApi } from "../environmentApi";
import { useRightPanelStore } from "../rightPanelStore";
import { cmmdApiClient } from "../environments/cmmdApi";
import { readPrimaryEnvironmentDescriptor } from "../environments/primary";
import { useSavedEnvironmentRuntimeStore } from "../environments/runtime/savedEnvironmentRuntimeStore";
import { newCommandId } from "../lib/utils";
import {
  resolvePullRequestLinkTarget,
  resolveCmmdProjectId,
  type ResolvedPullRequestLinkTarget,
} from "../lib/openPullRequestLink";
import { useProjectsAcrossEnvironments } from "../hooks/useProjectsAcrossEnvironments";
import { useProjectLinkListSnapshot } from "../cmmd/projectLinkListResource";
import { selectThreadByRef, useStore } from "../store";
import { toastManager } from "./ui/toast";
import {
  resolveExternalWebLinkHost,
  savedRuntimeSupportsThreadPullRequestLinking,
  showExternalLinkContextMenu,
} from "./chat/externalLinkContextMenu";
import {
  createHighlightCacheKey,
  estimateHighlightedSize,
  extractCodeBlock,
  extractFenceLanguage,
  nodeToPlainText,
} from "./ChatMarkdown.logic";

class CodeHighlightErrorBoundary extends React.Component<
  { fallback: ReactNode; children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { fallback: ReactNode; children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  override render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

interface ChatMarkdownProps {
  text: string;
  cwd: string | undefined;
  /** Execution environment that owns non-thread Markdown, such as a PR panel. */
  environmentId?: EnvironmentId;
  fileManagerRevealKind?: FileManagerRevealKind;
  /**
   * The thread whose right panel should show a clicked file.
   *
   * Omit it and a file link falls back to the external editor, which is what
   * every surface without a right panel wants. `workspaceRelativePath` is the
   * other half of the same decision: a file outside the workspace has no
   * in-app address, so it falls back too.
   */
  threadKey?: string;
  /**
   * Skills the active provider exposes. `$name` in a message renders as a chip
   * only for skills that actually exist — an unknown `$token` stays plain text,
   * so a shell variable in prose is not dressed up as something clickable.
   */
  skills?: ReadonlyArray<ServerProviderSkill>;
  isStreaming?: boolean;
}

const EMPTY_SKILLS: ReadonlyArray<ServerProviderSkill> = [];

const MAX_HIGHLIGHT_CACHE_ENTRIES = 500;
const MAX_HIGHLIGHT_CACHE_MEMORY_BYTES = 50 * 1024 * 1024;
const highlightedCodeCache = new LRUCache<string>(
  MAX_HIGHLIGHT_CACHE_ENTRIES,
  MAX_HIGHLIGHT_CACHE_MEMORY_BYTES,
);
const highlighterPromiseCache = new Map<string, Promise<DiffsHighlighter>>();

/**
 * Raw HTML in a message is model-authored, so it is parsed (`rehypeRaw`) and
 * then immediately scrubbed (`rehypeSanitize`) — never one without the other.
 * This is t3code's schema: `defaultSchema` plus the few data-attributes our own
 * plugins set, and `file:` hrefs so file links survive. `title` is dropped
 * globally because a model-supplied tooltip is unattributable text that appears
 * on hover with no indication it came from the model.
 */
function getHighlighterPromise(language: string): Promise<DiffsHighlighter> {
  const cached = highlighterPromiseCache.get(language);
  if (cached) return cached;

  const promise = getSharedHighlighter({
    themes: [resolveDiffThemeName("dark"), resolveDiffThemeName("light")],
    langs: [language as SupportedLanguages],
    preferredHighlighter: "shiki-js",
  }).catch((err) => {
    highlighterPromiseCache.delete(language);
    if (language === "text") {
      // "text" itself failed — Shiki cannot initialize at all, surface the error
      throw err;
    }
    // Language not supported by Shiki — fall back to "text"
    return getHighlighterPromise("text");
  });
  highlighterPromiseCache.set(language, promise);
  return promise;
}

/**
 * A fence's title line. t3code renders the file glyph from `pierre-icons`;
 * Forge's icon set is `vscode-icons`, so the same information comes from
 * `getVscodeIconUrlForEntry`. Language-only fences show the bare language
 * name — a generic file glyph next to the word "python" says nothing the word
 * did not already.
 */
function MarkdownCodeBlockTitle({
  fenceTitle,
  language,
  theme,
}: {
  readonly fenceTitle: string | null;
  readonly language: string;
  readonly theme: "light" | "dark";
}) {
  if (!fenceTitle) {
    return <span className="truncate">{language}</span>;
  }
  return (
    <>
      <img
        src={getVscodeIconUrlForEntry(fenceTitle, inferEntryKindFromPath(fenceTitle), theme)}
        alt=""
        aria-hidden="true"
        className="size-3.5 shrink-0"
      />
      <span className="truncate">{fenceTitle}</span>
    </>
  );
}

function MarkdownCodeBlock({
  code,
  language,
  fenceTitle,
  theme,
  children,
}: {
  code: string;
  language: string;
  fenceTitle: string | null;
  theme: "light" | "dark";
  children: ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleCopy = useCallback(() => {
    if (typeof navigator === "undefined" || navigator.clipboard == null) {
      return;
    }
    void navigator.clipboard
      .writeText(code)
      .then(() => {
        if (copiedTimerRef.current != null) {
          clearTimeout(copiedTimerRef.current);
        }
        setCopied(true);
        copiedTimerRef.current = setTimeout(() => {
          setCopied(false);
          copiedTimerRef.current = null;
        }, 1200);
      })
      .catch(() => undefined);
  }, [code]);

  useEffect(
    () => () => {
      if (copiedTimerRef.current != null) {
        clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = null;
      }
    },
    [],
  );

  return (
    <div className="chat-markdown-codeblock leading-snug">
      <div className="chat-markdown-codeblock-header">
        <MarkdownCodeBlockTitle fenceTitle={fenceTitle} language={language} theme={theme} />
      </div>
      <button
        type="button"
        className="chat-markdown-copy-button"
        onClick={handleCopy}
        title={copied ? "Copied" : "Copy code"}
        aria-label={copied ? "Copied" : "Copy code"}
      >
        {copied ? <CheckIcon className="size-3" /> : <CopyIcon className="size-3" />}
      </button>
      {children}
    </div>
  );
}

interface SuspenseShikiCodeBlockProps {
  className: string | undefined;
  code: string;
  themeName: DiffThemeName;
  isStreaming: boolean;
}

function SuspenseShikiCodeBlock({
  className,
  code,
  themeName,
  isStreaming,
}: SuspenseShikiCodeBlockProps) {
  const language = extractFenceLanguage(className);
  const cacheKey = createHighlightCacheKey(code, language, themeName);
  const cachedHighlightedHtml = !isStreaming ? highlightedCodeCache.get(cacheKey) : null;

  if (cachedHighlightedHtml != null) {
    return (
      <div
        className="chat-markdown-shiki"
        dangerouslySetInnerHTML={{ __html: cachedHighlightedHtml }}
      />
    );
  }

  const highlighter = use(getHighlighterPromise(language));
  const highlightedHtml = useMemo(() => {
    try {
      return highlighter.codeToHtml(code, { lang: language, theme: themeName });
    } catch (error) {
      // Log highlighting failures for debugging while falling back to plain text
      console.warn(
        `Code highlighting failed for language "${language}", falling back to plain text.`,
        error instanceof Error ? error.message : error,
      );
      // If highlighting fails for this language, render as plain text
      return highlighter.codeToHtml(code, { lang: "text", theme: themeName });
    }
  }, [code, highlighter, language, themeName]);

  useEffect(() => {
    if (!isStreaming) {
      highlightedCodeCache.set(
        cacheKey,
        highlightedHtml,
        estimateHighlightedSize(highlightedHtml, code),
      );
    }
  }, [cacheKey, code, highlightedHtml, isStreaming]);

  return (
    <div className="chat-markdown-shiki" dangerouslySetInnerHTML={{ __html: highlightedHtml }} />
  );
}

/**
 * Ported from t3code. Forge rendered `<table>` and `<details>` with the
 * browser's defaults: tables overflowed the message column with no way to read
 * a wide one or lift it out, and a `<details>` was a bare disclosure triangle.
 */
function MarkdownTable({ children, ...props }: React.ComponentProps<"table">) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expandLabel = expanded ? "Collapse table cells" : "Expand table cells";
  const copyLabel = copied ? "Copied" : "Copy table";

  function toggleExpanded() {
    const table = tableRef.current;
    if (!table) return;

    if (!expanded) {
      const rows = [...table.rows];
      const columnWidths = rows.reduce<number[]>((widths, row) => {
        [...row.cells].forEach((cell, columnIndex) => {
          widths[columnIndex] = Math.max(
            widths[columnIndex] ?? 0,
            cell.getBoundingClientRect().width,
          );
        });
        return widths;
      }, []);

      [...(table.tHead?.rows[0]?.cells ?? [])].forEach((cell, columnIndex) => {
        cell.style.minWidth = `${columnWidths[columnIndex] ?? cell.getBoundingClientRect().width}px`;
      });
    }

    setExpanded((value) => !value);
  }

  const handleCopy = useCallback((format: "markdown" | "csv") => {
    const table = containerRef.current?.querySelector("table");
    if (!table || typeof navigator === "undefined" || navigator.clipboard == null) {
      return;
    }
    const text =
      format === "markdown"
        ? serializeTableElementToMarkdown(table)
        : serializeTableElementToCsv(table);
    void navigator.clipboard
      .writeText(text)
      .then(() => {
        if (copiedTimerRef.current != null) {
          clearTimeout(copiedTimerRef.current);
        }
        setCopied(true);
        copiedTimerRef.current = setTimeout(() => {
          setCopied(false);
          copiedTimerRef.current = null;
        }, 1200);
      })
      .catch((cause) => {
        console.error("[chat-markdown] copy-table failed", { format }, cause);
      });
  }, []);

  useEffect(
    () => () => {
      if (copiedTimerRef.current != null) {
        clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = null;
      }
    },
    [],
  );

  return (
    <div
      ref={containerRef}
      className="chat-markdown-table-container"
      data-expanded={expanded ? "true" : "false"}
    >
      <ScrollArea scrollFade hideScrollbars className="w-full max-w-full rounded-none">
        <table ref={tableRef} {...props}>
          {children}
        </table>
      </ScrollArea>
      <div className="mt-0.5 flex items-center justify-between select-none">
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                className="chat-markdown-chrome-action"
                aria-pressed={expanded}
                onClick={toggleExpanded}
                aria-label={expandLabel}
              />
            }
          >
            {expanded ? <Minimize2Icon className="size-3" /> : <Maximize2Icon className="size-3" />}
          </TooltipTrigger>
          <TooltipPopup side="top">{expandLabel}</TooltipPopup>
        </Tooltip>
        <Menu>
          <Tooltip>
            <TooltipTrigger
              render={
                <MenuTrigger
                  render={
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      className="chat-markdown-chrome-action"
                      aria-label={copyLabel}
                    />
                  }
                />
              }
            >
              {copied ? <CheckIcon className="size-3" /> : <CopyIcon className="size-3" />}
            </TooltipTrigger>
            <TooltipPopup side="top">{copyLabel}</TooltipPopup>
          </Tooltip>
          <MenuPopup align="end">
            <MenuItem onClick={() => handleCopy("markdown")}>Copy as Markdown</MenuItem>
            <MenuItem onClick={() => handleCopy("csv")}>Copy as CSV</MenuItem>
          </MenuPopup>
        </Menu>
      </div>
    </div>
  );
}

function MarkdownDetails({
  children,
  open = false,
}: Pick<React.ComponentProps<"details">, "children" | "open">) {
  const [isOpen, setIsOpen] = useState(open);
  const childNodes = Children.toArray(children);
  const summaryIndex = childNodes.findIndex(
    (child) => isValidElement(child) && child.type === "summary",
  );
  const summaryNode = summaryIndex >= 0 ? childNodes[summaryIndex] : null;
  const summary =
    isValidElement<{ children?: ReactNode }>(summaryNode) && summaryNode.props.children
      ? summaryNode.props.children
      : "Details";
  const content = childNodes.filter((_, index) => index !== summaryIndex);

  return (
    <Collapsible
      defaultOpen={open}
      onOpenChange={setIsOpen}
      className="chat-markdown-details my-2 border-y border-border/60"
      data-markdown-details=""
      data-markdown-details-open={isOpen ? "true" : "false"}
    >
      <CollapsibleTrigger
        className="flex w-full items-center gap-2 py-2 text-left text-sm font-medium text-foreground data-panel-open:[&_svg]:rotate-90"
        data-markdown-details-summary=""
      >
        <ChevronRightIcon
          className="size-4 shrink-0 text-muted-foreground transition-transform"
          aria-hidden
        />
        <span>{summary}</span>
      </CollapsibleTrigger>
      <CollapsiblePanel>
        <div className="pb-3 ps-6 text-foreground/80" data-markdown-details-content="">
          {content}
        </div>
      </CollapsiblePanel>
    </Collapsible>
  );
}

function createPullRequestLinkClickHandler(input: {
  readonly target: ResolvedPullRequestLinkTarget | null;
  readonly threadRef: ReturnType<typeof parseScopedThreadKey>;
  readonly threadKey: string | undefined;
  readonly projectLinks: Parameters<typeof resolveCmmdProjectId>[0];
  readonly navigate: ReturnType<typeof useNavigate>;
}) {
  if (input.target === null) return undefined;
  const target = input.target;
  return (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (input.threadRef !== null && input.threadKey !== undefined) {
      void openPullRequestLinkInPanel({
        threadKey: input.threadKey,
        environmentId: input.threadRef.environmentId,
        target,
        cmmdProjectId: resolveCmmdProjectId(input.projectLinks, target),
      });
      return;
    }
    void input.navigate({
      to: "/pull-requests",
      search: {
        state: "all",
        provider: target.provider,
        environmentId: target.environmentId,
        projectId: target.projectId,
        host: target.host,
        repository: target.repository,
        number: target.number,
      },
    });
  };
}

function createExternalLinkContextHandler(input: {
  readonly href: string | undefined;
  readonly pullRequestThreadLink: ReturnType<typeof buildPullRequestThreadLinkAction>;
}) {
  if (!resolveExternalWebLinkHost(input.href) || !input.href) return undefined;
  return (event: React.MouseEvent<HTMLAnchorElement>) => {
    const api = readLocalApi();
    if (!api) return;
    event.preventDefault();
    void showExternalLinkContextMenu({
      href: input.href!,
      position: { x: event.clientX, y: event.clientY },
      canOpenInPreview: false,
      ...(input.pullRequestThreadLink === undefined
        ? {}
        : { pullRequestThreadLink: input.pullRequestThreadLink }),
      showContextMenu: (items, position) => api.contextMenu.show(items, position),
      openInPreview: async () => {},
      openExternal: async (url) => api.shell.openExternal(url),
      copyLink: (url) => navigator.clipboard.writeText(url),
      reportFailure: (operation, cause) => {
        if (operation === "update-thread-pull-request-link") {
          toastManager.add({
            type: "error",
            title: "Failed to update pull request link",
            description: cause instanceof Error ? cause.message : "An error occurred.",
          });
        }
      },
    });
  };
}

/**
 * Filename titles render icon + text; language-only titles render just the
 * icon (redundant next to its own name) and fall back to the language text
 * when no specific icon exists or it fails to load.
 */

const MarkdownRenderContext = createContext<{
  readonly cwd: ChatMarkdownProps["cwd"];
  readonly threadKey: string | undefined;
  readonly threadRef: ReturnType<typeof parseScopedThreadKey>;
  readonly projects: ReturnType<typeof useProjectsAcrossEnvironments>;
  readonly projectLinks: ReturnType<typeof useProjectLinkListSnapshot>;
  readonly linkedPullRequest: Parameters<typeof isLinkedPullRequest>[0];
  readonly navigate: ReturnType<typeof useNavigate>;
  readonly resolvedTheme: ReturnType<typeof useTheme>["resolvedTheme"];
  readonly diffThemeName: DiffThemeName;
  readonly openFileLink: (meta: MarkdownFileLinkMeta) => ReturnType<typeof openMarkdownFileLink>;
  readonly fileManagerRevealKind: FileManagerRevealKind | undefined;
  readonly canOpenFileLinks: boolean;
  readonly skills: readonly ServerProviderSkill[];
  readonly isStreaming: boolean;
}>(null!);

const MARKDOWN_COMPONENTS: Components = {
  a({ node: _node, href, ...props }) {
    const {
      cwd,
      threadRef,
      projects,
      linkedPullRequest,
      projectLinks,
      threadKey,
      navigate,
      resolvedTheme,
      openFileLink,
      fileManagerRevealKind,
    } = use(MarkdownRenderContext);
    const citation = href ? parseAssistantCitationHref(href) : null;
    if (citation) return <AssistantCitationChip citation={citation} />;
    const targetPath = resolveMarkdownFileLinkTarget(href, cwd);
    if (!targetPath) {
      const pullRequestTarget = href
        ? resolvePullRequestLinkTarget(
            href,
            threadRef === null
              ? projects
              : projects.filter((project) => project.environmentId === threadRef.environmentId),
          )
        : null;
      const pullRequestThreadLink = buildPullRequestThreadLinkAction({
        href,
        target: pullRequestTarget,
        threadRef,
        linkedPullRequest,
        cmmdProjectId:
          pullRequestTarget === null
            ? null
            : resolveCmmdProjectId(projectLinks.links, pullRequestTarget),
      });
      return (
        <a
          {...props}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={createPullRequestLinkClickHandler({
            target: pullRequestTarget,
            threadRef,
            threadKey,
            projectLinks: projectLinks.links,
            navigate,
          })}
          onContextMenu={createExternalLinkContextHandler({ href, pullRequestThreadLink })}
        />
      );
    }

    return (
      <ExplicitMarkdownFileLink
        href={href}
        cwd={cwd}
        anchorProps={props}
        copyMarkdown={`[${nodeToPlainText(props.children)}](${href ?? ""})`}
        theme={resolvedTheme === "dark" ? "dark" : "light"}
        onOpen={openFileLink}
        revealKind={fileManagerRevealKind}
      />
    );
  },
  p({ node: _node, children, ...props }) {
    const { skills } = use(MarkdownRenderContext);
    return <p {...props}>{renderSkillInlineMarkdownChildren(children, skills)}</p>;
  },
  li({ node: _node, children, ...props }) {
    const { skills } = use(MarkdownRenderContext);
    return <li {...props}>{renderSkillInlineMarkdownChildren(children, skills)}</li>;
  },
  blockquote({ node: _node, children, ...props }) {
    const alert =
      GITHUB_ALERT_PRESENTATIONS[String((props as Record<string, unknown>)["data-alert"] ?? "")];
    if (!alert) {
      return <blockquote {...props}>{children}</blockquote>;
    }
    // Not a <blockquote>: the stylesheet mutes those, and an alert's body is ordinary
    // text under a colored title — which is how the host renders it.
    return (
      <div role="note" className={cn("my-1 border-l-2 pl-3", alert.borderClassName)}>
        <p className={cn("flex items-center gap-1.5 font-medium", alert.titleClassName)}>
          <alert.Icon aria-hidden className="size-3.5 shrink-0" />
          {alert.label}
        </p>
        {children}
      </div>
    );
  },
  table({ node: _node, ...props }) {
    return <MarkdownTable {...props} />;
  },
  details({ node: _node, children, open: detailsOpen }) {
    return <MarkdownDetails open={detailsOpen}>{children}</MarkdownDetails>;
  },
  ol({ node, start, style, ...props }) {
    // Wide gutter once the numbers reach three digits, or the markers
    // overhang the text they belong to.
    const itemCount =
      node?.children?.filter((child) => child.type === "element" && child.tagName === "li")
        .length ?? 0;
    const gutterStyle = orderedListGutterStyle(itemCount, start);
    return (
      <ol {...props} start={start} style={gutterStyle ? { ...style, ...gutterStyle } : style} />
    );
  },
  code({ node, children, className, ...props }) {
    const { canOpenFileLinks, cwd, resolvedTheme, openFileLink } = use(MarkdownRenderContext);
    // `pre` handles fenced blocks; this only ever sees inline spans that
    // `remarkTagInlineCode` marked, so a fence can never become a chip.
    if (canOpenFileLinks && node?.properties?.["dataInlineCode"] != null) {
      const codeText = nodeToPlainText(children);
      const meta = resolveInlineCodeFileLinkMeta(codeText, cwd);
      if (meta) {
        return (
          <MarkdownFileLink
            meta={meta}
            copyMarkdown={`\`${codeText}\``}
            theme={resolvedTheme === "dark" ? "dark" : "light"}
            onOpen={openFileLink}
          />
        );
      }
    }
    return (
      <code {...props} className={className}>
        {children}
      </code>
    );
  },
  pre({ node, children, ...props }) {
    const { resolvedTheme, diffThemeName, isStreaming } = use(MarkdownRenderContext);
    const codeBlock = extractCodeBlock(children);
    if (!codeBlock) {
      return <pre {...props}>{children}</pre>;
    }

    const language = extractFenceLanguage(codeBlock.className);
    const fenceTitle = extractFenceTitle(extractPreCodeMeta(node));
    return (
      <MarkdownCodeBlock
        code={codeBlock.code}
        language={language}
        fenceTitle={fenceTitle}
        theme={resolvedTheme}
      >
        <CodeHighlightErrorBoundary fallback={<pre {...props}>{children}</pre>}>
          <Suspense fallback={<pre {...props}>{children}</pre>}>
            <SuspenseShikiCodeBlock
              className={codeBlock.className}
              code={codeBlock.code}
              themeName={diffThemeName}
              isStreaming={isStreaming}
            />
          </Suspense>
        </CodeHighlightErrorBoundary>
      </MarkdownCodeBlock>
    );
  },
};

function ChatMarkdown({
  text,
  cwd,
  environmentId,
  fileManagerRevealKind,
  threadKey,
  skills = EMPTY_SKILLS,
  isStreaming = false,
}: ChatMarkdownProps) {
  const navigate = useNavigate();
  const projects = useProjectsAcrossEnvironments();
  const projectLinks = useProjectLinkListSnapshot();
  const threadRef = threadKey === undefined ? null : parseScopedThreadKey(threadKey);
  const linkedPullRequest = useStore((store) =>
    threadRef === null ? null : (selectThreadByRef(store, threadRef)?.linkedPullRequest ?? null),
  );
  const { resolvedTheme } = useTheme();
  const diffThemeName = resolveDiffThemeName(resolvedTheme);
  const markdownUrlTransform = useCallback((href: string) => {
    return parseAssistantCitationHref(href)
      ? href
      : (rewriteMarkdownFileUriHref(href) ?? defaultUrlTransform(href));
  }, []);
  // Only render a file chip when there is somewhere for it to go. A chip is an
  // affordance; one that silently does nothing is worse than leaving the span
  // as plain code, and inline references are far more numerous than explicit
  // links, so the failure would be everywhere at once.
  const executionApi = environmentId === undefined ? undefined : readEnvironmentApi(environmentId);
  const canOpenFileLinks = executionApi !== undefined || readLocalApi() !== null;
  const openFileLink = useCallback(
    (meta: MarkdownFileLinkMeta) => openMarkdownFileLink({ meta, threadKey, executionApi }),
    [executionApi, threadKey],
  );

  const renderContext = useMemo(
    () => ({
      cwd,
      threadKey,
      threadRef,
      projects,
      projectLinks,
      linkedPullRequest,
      navigate,
      resolvedTheme,
      diffThemeName,
      openFileLink,
      fileManagerRevealKind,
      canOpenFileLinks,
      skills,
      isStreaming,
    }),
    [
      cwd,
      threadKey,
      threadRef,
      projects,
      projectLinks,
      linkedPullRequest,
      navigate,
      resolvedTheme,
      diffThemeName,
      openFileLink,
      fileManagerRevealKind,
      canOpenFileLinks,
      skills,
      isStreaming,
    ],
  );
  return (
    <MarkdownRenderContext value={renderContext}>
      <div className="chat-markdown w-full min-w-0 text-sm leading-relaxed text-foreground/80">
        <ReactMarkdown
          remarkPlugins={[
            remarkGfm,
            remarkGithubAlerts,
            remarkNormalizeListItemIndentation,
            remarkPreserveCodeMeta,
            remarkTagInlineCode,
          ]}
          rehypePlugins={[rehypeRaw, [rehypeSanitize, CHAT_MARKDOWN_SANITIZE_SCHEMA]]}
          components={MARKDOWN_COMPONENTS}
          urlTransform={markdownUrlTransform}
        >
          {text}
        </ReactMarkdown>
      </div>
    </MarkdownRenderContext>
  );
}

function buildPullRequestThreadLinkAction(input: {
  readonly href: string | undefined;
  readonly target: ResolvedPullRequestLinkTarget | null;
  readonly threadRef: ReturnType<typeof parseScopedThreadKey> | null;
  readonly linkedPullRequest: Parameters<typeof isLinkedPullRequest>[0];
  readonly cmmdProjectId: number | null;
}) {
  if (
    input.target === null ||
    input.threadRef === null ||
    input.href === undefined ||
    !environmentSupportsThreadPullRequestLinking(input.threadRef.environmentId)
  ) {
    return undefined;
  }
  const unlinking = isLinkedPullRequest(input.linkedPullRequest, input.target);
  return {
    id: unlinking ? ("unlink-pull-request" as const) : ("link-pull-request" as const),
    label: unlinking ? "Unlink pull request from thread" : "Link pull request to thread",
    update: () =>
      updateThreadPullRequestLink({
        href: input.href!,
        target: input.target!,
        cmmdProjectId: input.cmmdProjectId,
        threadRef: input.threadRef!,
        unlinking,
      }),
  };
}

async function updateThreadPullRequestLink(input: {
  readonly href: string;
  readonly target: ResolvedPullRequestLinkTarget;
  readonly threadRef: NonNullable<ReturnType<typeof parseScopedThreadKey>>;
  readonly unlinking: boolean;
  readonly cmmdProjectId: number | null;
}): Promise<void> {
  const api = readEnvironmentApi(input.threadRef.environmentId);
  if (!api) throw new Error("This thread is no longer available to update.");
  const projectRepositoryId = input.unlinking
    ? null
    : input.cmmdProjectId === null
      ? null
      : await resolveCanonicalRepositoryLinkId(input.cmmdProjectId, input.target.repository);
  if (!input.unlinking && projectRepositoryId === null) {
    throw new Error("This pull request has no canonical Command repository link.");
  }
  await api.orchestration.dispatchCommand({
    type: "thread.meta.update",
    commandId: newCommandId(),
    threadId: input.threadRef.threadId,
    linkedPullRequest: input.unlinking
      ? null
      : {
          projectId: input.target.projectId,
          projectRepositoryId: projectRepositoryId!,
          provider: input.target.provider,
          host: input.target.host,
          repository: input.target.repository,
          number: input.target.number,
          url: input.href,
        },
  });
}

async function openPullRequestLinkInPanel(input: {
  readonly threadKey: string;
  readonly environmentId: NonNullable<ReturnType<typeof parseScopedThreadKey>>["environmentId"];
  readonly target: NonNullable<ReturnType<typeof resolvePullRequestLinkTarget>>;
  readonly cmmdProjectId: number | null;
}): Promise<void> {
  try {
    if (input.cmmdProjectId === null) {
      toastManager.add({
        type: "warning",
        title: "Pull request details unavailable",
        description: "This Forge project is not linked to a CMMD project.",
      });
      return;
    }
    const repositoryLinkId = await resolveCanonicalRepositoryLinkId(
      input.cmmdProjectId,
      input.target.repository,
    );
    if (repositoryLinkId === null) {
      toastManager.add({
        type: "warning",
        title: "Pull request details unavailable",
        description: "This pull request has no canonical Command repository link.",
      });
      return;
    }
    useRightPanelStore.getState().openPullRequest(input.threadKey, {
      provider: input.target.provider,
      environmentId: input.environmentId,
      repositoryLinkId: String(repositoryLinkId),
      projectId: input.target.projectId,
      host: input.target.host,
      repository: input.target.repository,
      number: input.target.number,
    });
  } catch (cause) {
    console.error("[chat-markdown] resolving pull request repository link failed", cause);
    toastManager.add({
      type: "error",
      title: "Failed to open pull request details",
      description: cause instanceof Error ? cause.message : "An error occurred.",
    });
  }
}

async function resolveCanonicalRepositoryLinkId(
  cmmdProjectId: number,
  repository: string,
): Promise<number | null> {
  const repositories = await cmmdApiClient.listProjectRepositories(String(cmmdProjectId));
  const target = normalizeRepositoryName(repository);
  const match = repositories.find(
    (candidate) => normalizeRepositoryName(candidate.repoFullName) === target,
  );
  return match === undefined ? null : match.id;
}

function normalizeRepositoryName(repository: string): string {
  return repository
    .trim()
    .replace(/\.git$/i, "")
    .toLowerCase();
}

function isLinkedPullRequest(
  linkedPullRequest: {
    readonly projectId: string;
    readonly repository: string;
    readonly number: number;
  } | null,
  target: { readonly projectId: string; readonly repository: string; readonly number: number },
): boolean {
  return (
    linkedPullRequest?.projectId === target.projectId &&
    linkedPullRequest.repository.localeCompare(target.repository, undefined, {
      sensitivity: "accent",
    }) === 0 &&
    linkedPullRequest.number === target.number
  );
}

function environmentSupportsThreadPullRequestLinking(
  environmentId: NonNullable<ReturnType<typeof parseScopedThreadKey>>["environmentId"],
): boolean {
  const primary = readPrimaryEnvironmentDescriptor();
  if (primary?.environmentId === environmentId) {
    return primary.capabilities.threadPullRequestLinking === true;
  }
  return savedRuntimeSupportsThreadPullRequestLinking(
    useSavedEnvironmentRuntimeStore.getState().byId[environmentId],
  );
}

export default memo(ChatMarkdown);
